import React, { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { motion } from "framer-motion";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { consumeTransientDashboardMagicLink } from "@/lib/transient-magic-link";

function NoTokenState() {
  const [, navigate] = useLocation();
  return (
    <div className="container mx-auto max-w-2xl px-4 pt-32 pb-20 text-center">
      <h1 className="text-3xl font-bold tracking-tighter mb-4 uppercase text-zinc-100">
        Link Not Found
      </h1>
      <p className="text-zinc-400 font-sans mb-8">
        We could not locate a management link for this session. If you recently posted a role, check your email for the link.
      </p>
      <Button
        onClick={() => navigate("/jobs/new")}
        className="bg-primary hover:bg-primary/90 text-white font-bold tracking-wide uppercase"
      >
        Post an Opportunity
      </Button>
    </div>
  );
}

export default function JobSuccessPage() {
  useDocumentTitle("Opportunity Posted");
  const isReduced = useReducedMotion();
  const [, navigate] = useLocation();

  const [token] = useState<string | null>(() => sessionStorage.getItem("mgmt_token"));
  const [companyName] = useState<string>(() => sessionStorage.getItem("mgmt_company") ?? "");
  const [dashboardMagicLink] = useState<string | null>(() => consumeTransientDashboardMagicLink());

  const [copied, setCopied] = useState(false);

  useEffect(() => {
    sessionStorage.removeItem("mgmt_token");
    sessionStorage.removeItem("mgmt_company");
  }, []);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  if (!token) {
    return <NoTokenState />;
  }

  const managementLink = `${window.location.origin}/jobs/manage?token=${encodeURIComponent(token)}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(managementLink).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => {
        window.prompt("Copy this link:", managementLink);
      }
    );
  };

  return (
    <div className="container mx-auto max-w-2xl px-4 pt-24 pb-20">
      <motion.div
        initial={isReduced ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: isReduced ? 0 : 0.4 }}
      >
        <div className="mb-2">
          <span className="text-xs font-bold tracking-widest uppercase text-primary">
            Opportunity Live
          </span>
        </div>
        <h1 className="text-4xl font-bold tracking-tighter mb-3 uppercase">
          Your listing is live.
        </h1>
        <p className="text-zinc-400 mb-8 font-sans">
          Your opportunity is now visible to Carry Network members. Save the management link below — it is the only way to edit or remove your listing. We have also sent it to your email address.
        </p>

        <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-8 backdrop-blur space-y-6">
          <div>
            <p className="text-sm font-medium text-zinc-300 mb-2">Your management link</p>
            <p className="text-xs text-zinc-500 font-sans mb-3">
              Keep this link private. Anyone with it can edit or delete your listing.
            </p>
            <div className="flex gap-2">
              <Input
                readOnly
                value={managementLink}
                className="bg-zinc-950 border-white/10 text-zinc-300 font-mono text-sm flex-1 cursor-text select-all"
                onFocus={(e) => e.target.select()}
              />
              <Button
                type="button"
                onClick={handleCopy}
                className={`shrink-0 font-bold tracking-wide uppercase transition-colors ${
                  copied
                    ? "bg-green-700 hover:bg-green-700 text-white"
                    : "bg-primary hover:bg-primary/90 text-white"
                }`}
              >
                {copied ? "Copied!" : "Copy Link"}
              </Button>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 flex flex-wrap gap-3">
            {dashboardMagicLink ? (
              <Button
                type="button"
                onClick={() => { window.location.href = dashboardMagicLink; }}
                className="bg-primary hover:bg-primary/90 text-white font-bold tracking-wide uppercase"
              >
                Access your Job Management Dashboard
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => window.open(managementLink, "_blank", "noopener,noreferrer")}
                className="bg-primary hover:bg-primary/90 text-white font-bold tracking-wide uppercase"
              >
                Manage Listing
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              className="border-white/10 hover:bg-white/5 font-sans text-zinc-400"
              onClick={() => navigate("/?tab=opportunities")}
            >
              View Opportunities
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
